/* eslint-disable @typescript-eslint/no-explicit-any */
import { GlobalCurrencyFilterDTO } from '@/@dtos/GlobalSearchDTO';
import { ToastOptions, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getBrandListById } from '@/CommonApis/BrandListById';
import { ResponseDTO } from '@/@dtos/ResponseDTO';
import JsFileDownloader from 'js-file-downloader';
import { removeEmpty } from '@/utils/removeEmpty';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { isMockAuthEnabled } from '@/config/authMode';
import { authTokenKind, isAccessTokenMode, selectSessionToken } from '@/config/authToken';
import { hasSessionHint, readAccessTokenCopy, storeAccessTokenCopy } from '@/utils/accessTokenCopy';
import { getAuthSession } from '@/auth/sessionRegistry';
import { isSigningOut, markSigningOut } from '@/config/signOutState';
import { identityApiUrl, isCookieSessionMode } from '@/config/authSession';
import {
  NoAccessError,
  assignmentFromLoginContext,
  expectedAssignmentHeaders,
  getActiveAssignment,
  getActiveContextType,
  getPreviewContextType,
  getTabAssignment,
  setActiveAssignment,
  withActiveContextType,
} from '@/auth/activeAssignment';
import { getHomePartnerId, homePath, setHomePartnerId } from '@/auth/landing';
import { readRolePage, returnPageFor } from '@/auth/rolePages';
import { clearSession } from '@/auth/clearSession';
import { announceSignOut, isRoleChangedPayload, roleAwareFetch } from '@/auth/roleChange';
import {
  getSessionCommonData,
  getSessionModules,
  getSessionUser,
  isSignedIn,
  readSessionData,
  roleSessionLoaded,
  sessionHasPermission,
  setSessionCommonData,
  setSessionModules,
  setSessionUser,
  SESSION_GC_TIME,
  writeSessionData,
} from '@/auth/session';
import { queryClient } from '@/query/queryClient';
import { activeAssignmentId, roleSessionKeys, sessionKeys } from '@/query/keys';
import {
  type StoredHeaderSelection,
  getHeaderBrandId,
  getHeaderFilters,
  getHeaderPropertyId,
  getHeaderSelection,
  hydrateHeaderSelection,
  isHeaderSelectionLoaded,
  parseHeaderFilters,
  setHeaderSelectionSaver,
  toStoredSelection,
} from '@/selection/headerSelection';

type ApiErrorMessage = string | { [key: string]: string[] };

/** Option shared by the authenticated-request helpers: when true, the request
 * flow has already attempted a forced InitiateAuth refresh before deciding the
 * session is dead (see FIX AE-I2596 / confirmed-failure logout). */
interface AuthenticatedFetchOptions {
  skipUnauthorizedHandling?: boolean;
  requestLabel?: string;
  redirectOnUnauthorized?: boolean;
  forcedRefreshAttempted?: boolean;
}

/** What a page load learns about the signed-in user (ApiProvider.requestSessionUser). */
export type SessionUserAnswer =
  | { status: 'ok'; data: Record<string, any> }
  | { status: 'signed_out' }
  | { status: 'refused'; httpStatus: number }
  | { status: 'unavailable' };

export class ApiProvider {
  static apiUrl = import.meta.env.VITE_API_URL;

  static token = readAccessTokenCopy();
  private static readonly SESSION_EXPIRED_MESSAGE_KEY = 'session_expired_message';
  private static readonly SESSION_EXPIRED_MESSAGE_TEXT =
    'Your session has expired. Please log in again.';
  private static readonly SESSION_PROBE_INTERVAL_MS = 15000;
  private static isSessionRedirectInProgress = false;
  private static lastSessionProbeAt = 0;
  private static sessionProbeInFlight: Promise<boolean> | null = null;
  private static authTokenFetchInFlight: Promise<string | null> | null = null;
  private static initiateAuthRefreshInFlight: Promise<string | null> | null = null;
  private static amplifyRefreshInFlight: Promise<string | null> | null = null;

  private static isAuthDebugEnabled(): boolean {
    try {
      const explicitFlag =
        localStorage.getItem('auth_debug') || sessionStorage.getItem('auth_debug');
      if (explicitFlag === '1' || explicitFlag === 'true') {
        return true;
      }
    } catch {
      // Ignore storage failures.
    }

    if (typeof window === 'undefined') {
      return false;
    }

    const host = window.location.hostname || '';
    return host.includes('localhost') || host.includes('127.0.0.1') || host.includes('dev-engine');
  }

  private static authDebug(message: string, meta?: Record<string, any>): void {
    if (!ApiProvider.isAuthDebugEnabled()) return;

    const timestamp = new Date().toISOString();
    if (meta) {
      console.debug('AUTH-DEBUG', {
        timestamp,
        message,
        meta: ApiProvider.toDebugSnapshot(meta),
      });
    } else {
      console.debug('AUTH-DEBUG', { timestamp, message });
    }
  }

  private static toDebugSnapshot(value: any, depth = 4): any {
    if (depth <= 0) {
      return '[max-depth]';
    }

    if (value === null || value === undefined) {
      return value;
    }

    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      return value;
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
      };
    }

    if (Array.isArray(value)) {
      const maxItems = 30;
      return {
        length: value.length,
        items: value.slice(0, maxItems).map((item) => ApiProvider.toDebugSnapshot(item, depth - 1)),
        truncated: value.length > maxItems,
      };
    }

    if (t === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) {
        out[k] = ApiProvider.toDebugSnapshot(v, depth - 1);
      }
      return out;
    }

    return String(value);
  }

  private static amplifyStorageDebugMeta(): Record<string, any> {
    try {
      const clientId = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
      const isCognitoKey = (key: string) =>
        key.includes('CognitoIdentityServiceProvider') ||
        (clientId !== '' && key.includes(clientId));

      const allKeys = Object.keys(localStorage).sort();
      const cognitoKeys = allKeys.filter(isCognitoKey);
      const lowerKeys = cognitoKeys.map((key) => key.toLowerCase());
      const findKey = (needle: string) => lowerKeys.some((key) => key.includes(needle));

      // Also inspect sessionStorage so we can see where Amplify actually stored the tokens.
      let ssResult: Record<string, any> = { error: 'unavailable' };
      try {
        const ssAllKeys = Object.keys(sessionStorage).sort();
        const ssCognitoKeys = ssAllKeys.filter(isCognitoKey);
        const ssLower = ssCognitoKeys.map((k) => k.toLowerCase());
        const findSsKey = (needle: string) => ssLower.some((k) => k.includes(needle));
        ssResult = {
          totalSessionStorageKeys: ssAllKeys.length,
          cognitoKeyCount: ssCognitoKeys.length,
          hasLastAuthUserKey: findSsKey('lastauthuser'),
          hasRefreshTokenKey: findSsKey('refreshtoken'),
          hasAccessTokenKey: findSsKey('accesstoken'),
          hasIdTokenKey: findSsKey('idtoken'),
          sampleCognitoKeys: ssCognitoKeys.slice(0, 20),
        };
      } catch {
        /* ignore */
      }

      return {
        totalLocalStorageKeys: allKeys.length,
        cognitoKeyCount: cognitoKeys.length,
        hasLastAuthUserKey: findKey('lastauthuser'),
        hasRefreshTokenKey: findKey('refreshtoken'),
        hasAccessTokenKey: findKey('accesstoken'),
        hasIdTokenKey: findKey('idtoken'),
        sampleCognitoKeys: cognitoKeys.slice(0, 20),
        sessionStorage: ssResult,
      };
    } catch (error: any) {
      return {
        error: error?.message || 'storage-unavailable',
      };
    }
  }

  /**
   * Read a Cognito token-store value from wherever Amplify persisted it.
   * Amplify v6 defaults to localStorage but may fall back to sessionStorage in
   * some browser/extension environments, so we check both.
   */
  private static getCognitoStoreValue(key: string): string | null {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) return v;
    } catch {
      /* ignore */
    }
    try {
      const v = sessionStorage.getItem(key);
      if (v !== null) return v;
    } catch {
      /* ignore */
    }
    return null;
  }

  private static setCognitoStoreValue(key: string, value: string): void {
    // Write to whichever store currently holds this key; fall back to localStorage.
    let wroteToSs = false;
    try {
      if (sessionStorage.getItem(key) !== null) {
        sessionStorage.setItem(key, value);
        wroteToSs = true;
      }
    } catch {
      /* ignore */
    }
    try {
      if (!wroteToSs || localStorage.getItem(key) !== null) {
        localStorage.setItem(key, value);
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Refresh the bearer token for the configured token kind (VITE_AUTH_TOKEN_KIND).
   *
   * - `id` (default): Amplify's own refresh drops the IdToken for native sign-in
   *   (see refreshIdTokenViaInitiateAuth), so we always use the classic
   *   InitiateAuth REFRESH_TOKEN_AUTH call. Unchanged behaviour.
   * - `access`: Amplify's refresh (GetTokensFromRefreshToken) keeps the access
   *   token and persists a rotated refresh token itself, so use it first. If it
   *   yields nothing (no session, network error) fall back to InitiateAuth, which
   *   also returns an AccessToken and carries the retry + DEVICE_KEY handling.
   *
   * Both paths keep the resulting token in the in-memory copy (accessTokenCopy).
   */
  private static async refreshAuthToken(): Promise<string | null> {
    if (!isAccessTokenMode) {
      return ApiProvider.refreshIdTokenViaInitiateAuth();
    }

    const viaAmplify = await ApiProvider.refreshAccessTokenViaAmplify();
    if (viaAmplify) return viaAmplify;

    ApiProvider.authDebug(
      'refreshAuthToken(): Amplify refresh yielded no access token; trying InitiateAuth',
    );
    return ApiProvider.refreshIdTokenViaInitiateAuth();
  }

  /**
   * Access-token mode only: force an Amplify refresh and return the new access
   * token. Deduped so concurrent callers share one Cognito round-trip.
   */
  private static async refreshAccessTokenViaAmplify(): Promise<string | null> {
    if (ApiProvider.amplifyRefreshInFlight) {
      ApiProvider.authDebug('refreshAccessTokenViaAmplify(): reusing in-flight refresh');
      return ApiProvider.amplifyRefreshInFlight;
    }

    ApiProvider.amplifyRefreshInFlight = (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const accessToken = selectSessionToken(session.tokens, 'access');
        ApiProvider.authDebug('refreshAccessTokenViaAmplify(): forceRefresh completed', {
          session: ApiProvider.amplifySessionDebugMeta(session),
        });
        if (!accessToken) return null;

        try {
          storeAccessTokenCopy(accessToken);
        } catch {
          /* ignore */
        }
        return accessToken;
      } catch (error: any) {
        ApiProvider.authDebug('refreshAccessTokenViaAmplify(): forceRefresh threw', {
          name: error?.name,
          message: error?.message,
        });
        return null;
      }
    })();

    try {
      return await ApiProvider.amplifyRefreshInFlight;
    } finally {
      ApiProvider.amplifyRefreshInFlight = null;
    }
  }

  /**
   * Refresh Cognito tokens by calling InitiateAuth REFRESH_TOKEN_AUTH directly.
   *
   * Background: Amplify v6.10+ switched its internal token-refresh path to the
   * newer GetTokensFromRefreshToken Cognito API. That API does NOT return an
   * IdToken for native (username/password) auth flows, so after every Amplify
   * refresh, storeTokens() deletes the stored IdToken. In ID-token mode the
   * backend only accepts IdTokens (token_use === 'id'), so we bypass Amplify and
   * call the classic InitiateAuth REFRESH_TOKEN_AUTH endpoint directly — this
   * always returns both AccessToken and IdToken.
   *
   * Returns the fresh bearer token for the configured kind (IdToken in `id`
   * mode, AccessToken in `access` mode) on success, or null on failure.
   */
  private static async refreshIdTokenViaInitiateAuth(): Promise<string | null> {
    // Dedupe layer: every concurrent caller (parallel 401 handling, route/focus
    // session probes) shares ONE in-flight InitiateAuth call instead of firing
    // N separate refresh requests at Cognito. This mirrors getAuthToken()'s
    // authTokenFetchInFlight pattern so 10 parallel failed calls cannot trigger
    // 10 simultaneous refresh attempts.
    if (ApiProvider.initiateAuthRefreshInFlight) {
      ApiProvider.authDebug('refreshIdTokenViaInitiateAuth(): reusing in-flight refresh');
      return ApiProvider.initiateAuthRefreshInFlight;
    }

    ApiProvider.initiateAuthRefreshInFlight = ApiProvider.performRefreshIdTokenViaInitiateAuth();
    try {
      return await ApiProvider.initiateAuthRefreshInFlight;
    } finally {
      ApiProvider.initiateAuthRefreshInFlight = null;
    }
  }

  /**
   * Actual InitiateAuth REFRESH_TOKEN_AUTH network flow, called through the
   * dedupe wrapper refreshIdTokenViaInitiateAuth() so only one of these can run
   * at a time.
   *
   * Sends DEVICE_KEY when Amplify stored one for this user, and retries once
   * (after a short delay) on transient network errors / HTTP 5xx. It does NOT
   * retry NotAuthorizedException: that error means the refresh token is
   * genuinely revoked/expired, so retrying would only add latency.
   */
  private static async performRefreshIdTokenViaInitiateAuth(): Promise<string | null> {
    // Cookie session mode: there is no refresh token in the browser to send;
    // Amplify's refresher (installed by the shared kit) asks the identity
    // service instead, so the InitiateAuth fallback does not apply.
    if (isCookieSessionMode) return null;

    const clientId = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
    const userPoolId = String(import.meta.env.VITE_COGNITO_USER_POOL_ID || '').trim();
    if (!clientId || !userPoolId) return null;

    const region = userPoolId.split('_')[0];
    if (!region || !region.includes('-')) return null;

    // Locate the last authenticated user from Amplify's storage.
    const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
    const username = ApiProvider.getCognitoStoreValue(lastAuthUserKey);
    if (!username) return null;

    // Locate the stored refresh token.
    const refreshTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`;
    const refreshToken = ApiProvider.getCognitoStoreValue(refreshTokenKey);
    if (!refreshToken) return null;

    // If Amplify persisted a device key for this user, send it so Cognito can
    // also refresh device-scoped sessions (same storage pattern as refreshToken).
    const deviceKeyKey = `CognitoIdentityServiceProvider.${clientId}.${username}.deviceKey`;
    const deviceKey = ApiProvider.getCognitoStoreValue(deviceKeyKey);

    ApiProvider.authDebug(
      'refreshIdTokenViaInitiateAuth(): calling InitiateAuth REFRESH_TOKEN_AUTH',
      {
        region,
        clientId,
        username,
        hasDeviceKey: Boolean(deviceKey),
        maxAttempts: 2,
      },
    );

    const waitBeforeRetry = () => new Promise((resolve) => setTimeout(resolve, 600));

    for (let attempt = 1; attempt <= 2; attempt++) {
      const tryRequest = async () => {
        const authParameters: Record<string, string> = { REFRESH_TOKEN: refreshToken };
        if (deviceKey) {
          authParameters.DEVICE_KEY = deviceKey;
        }

        const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            'Cache-Control': 'no-store',
          },
          body: JSON.stringify({
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            ClientId: clientId,
            AuthParameters: authParameters,
          }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          return {
            ok: false as const,
            status: response.status,
            errorCode: (errBody as any)?.__type ?? null,
            errBody,
          };
        }

        const data = await response.json();
        return { ok: true as const, data };
      };

      let outcome: Awaited<ReturnType<typeof tryRequest>>;
      try {
        outcome = await tryRequest();
      } catch (error: any) {
        // Network-level failure (DNS, TLS, timeout, connection reset, ...).
        // Retry once after a short delay; this is exactly the transient case
        // where a single attempt would otherwise look like a dead session.
        ApiProvider.authDebug('refreshIdTokenViaInitiateAuth(): fetch threw', {
          name: error?.name,
          message: error?.message,
          attempt,
        });

        if (attempt < 2) {
          await waitBeforeRetry();
          continue;
        }
        return null;
      }

      if (!outcome.ok) {
        const errorCode = typeof outcome.errorCode === 'string' ? outcome.errorCode : null;
        const isNotAuthorized =
          errorCode !== null && errorCode.indexOf('NotAuthorizedException') !== -1;

        ApiProvider.authDebug(
          'refreshIdTokenViaInitiateAuth(): InitiateAuth returned non-OK status',
          {
            status: outcome.status,
            errorCode,
            message: (outcome.errBody as any)?.message ?? null,
            attempt,
            willRetry: !isNotAuthorized && outcome.status >= 500 && attempt < 2,
          },
        );

        // NotAuthorizedException means the refresh token is genuinely
        // revoked/expired. Retrying cannot help, so surface the failure now.
        if (isNotAuthorized) return null;

        // Retry once on transient server errors (HTTP 5xx) after a short delay.
        if (outcome.status >= 500 && attempt < 2) {
          await waitBeforeRetry();
          continue;
        }

        return null;
      }

      const idToken: string | undefined = (outcome.data as any)?.AuthenticationResult?.IdToken;
      const accessToken: string | undefined = (outcome.data as any)?.AuthenticationResult
        ?.AccessToken;
      const newRefreshToken: string | undefined = (outcome.data as any)?.AuthenticationResult
        ?.RefreshToken;

      ApiProvider.authDebug('refreshIdTokenViaInitiateAuth(): InitiateAuth succeeded', {
        idToken: ApiProvider.tokenDebugMeta(idToken),
        accessToken: ApiProvider.tokenDebugMeta(accessToken),
        hasNewRefreshToken: Boolean(newRefreshToken),
        tokenKind: authTokenKind,
        attempt,
      });

      // The token we hand to the API depends on VITE_AUTH_TOKEN_KIND.
      const bearerToken = isAccessTokenMode ? accessToken : idToken;
      if (!bearerToken) return null;

      // Write the new tokens back into Amplify's storage so subsequent
      // fetchAuthSession() calls see fresh tokens.
      const idTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`;
      const accessTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`;

      if (idToken) ApiProvider.setCognitoStoreValue(idTokenKey, idToken);
      if (accessToken) ApiProvider.setCognitoStoreValue(accessTokenKey, accessToken);

      // Refresh-token rotation: when the app client has rotation enabled,
      // Cognito returns a NEW RefreshToken in every REFRESH_TOKEN_AUTH response
      // and the previous one is revoked (after a short grace window). Persist
      // it under the same Amplify key we read from, otherwise the next refresh
      // would replay the revoked token and log the user out. When rotation is
      // off, RefreshToken is absent and the stored one stays valid.
      if (newRefreshToken) ApiProvider.setCognitoStoreValue(refreshTokenKey, newRefreshToken);

      try {
        storeAccessTokenCopy(bearerToken);
      } catch {
        /* ignore */
      }

      return bearerToken;
    }

    return null;
  }

  private static amplifySessionDebugMeta(session: any): Record<string, any> {
    const idToken = session?.tokens?.idToken?.toString?.();
    const accessToken = session?.tokens?.accessToken?.toString?.();

    return {
      hasTokens: Boolean(session?.tokens),
      hasIdentityId: Boolean(session?.identityId),
      hasCredentials: Boolean(session?.credentials),
      idToken: ApiProvider.tokenDebugMeta(idToken),
      accessToken: ApiProvider.tokenDebugMeta(accessToken),
    };
  }

  private static tokenDebugMeta(token?: string | null): Record<string, any> {
    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    const now = Math.floor(Date.now() / 1000);
    const exp = Number(payload?.exp ?? NaN);
    const iat = Number(payload?.iat ?? NaN);

    return {
      present: Boolean(token),
      length: token ? token.length : 0,
      exp: Number.isFinite(exp) ? exp : null,
      expInSec: Number.isFinite(exp) ? exp - now : null,
      iat: Number.isFinite(iat) ? iat : null,
      iatAgeSec: Number.isFinite(iat) ? now - iat : null,
      token_use: payload?.token_use ?? null,
      aud: payload?.aud ?? null,
      subSuffix: typeof payload?.sub === 'string' ? payload.sub.slice(-8) : null,
      cognitoUsername: payload?.['cognito:username'] ?? null,
      username: payload?.username ?? null,
      email: payload?.email ?? null,
      scope: payload?.scope ?? null,
    };
  }

  /**
   * Get a valid authentication token for API requests.
   *
   * Always fetches from Amplify so that expired Cognito IdTokens are
   * silently refreshed via the Cognito refresh token — users stay logged
   * in for the full refresh-token lifetime rather than only the 1-hour
   * IdToken TTL.
   *
   * In local mock mode (no real Cognito session) we fall back to whatever
   * is stored in localStorage.
   */
  static async getAuthToken(): Promise<string | null> {
    if (ApiProvider.authTokenFetchInFlight) {
      ApiProvider.authDebug('getAuthToken(): reusing in-flight token fetch');
      return ApiProvider.authTokenFetchInFlight;
    }

    ApiProvider.authDebug('getAuthToken(): starting token resolution', {
      hasLocalUserData: isSignedIn(),
      localStorageToken: ApiProvider.tokenDebugMeta(readAccessTokenCopy()),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    ApiProvider.authTokenFetchInFlight = ApiProvider.resolveAuthToken();
    try {
      const token = await ApiProvider.authTokenFetchInFlight;
      ApiProvider.authDebug('getAuthToken(): token resolution completed', {
        resolvedToken: ApiProvider.tokenDebugMeta(token),
      });
      return token;
    } finally {
      ApiProvider.authTokenFetchInFlight = null;
    }
  }

  /** The longest a role change waits for a token of the new role (ensureTokenForAssignment). */
  static readonly TOKEN_FOR_ASSIGNMENT_TIMEOUT_MS = 8000;

  /** The role assignment a token was issued for (`pnet:ra`, pre-token Lambda), or null. */
  private static tokenAssignmentId(token?: string | null): string | null {
    const ra = ApiProvider.decodeJwtPayloadSafe(token)?.['pnet:ra'];
    return ra === undefined || ra === null || ra === '' ? null : String(ra);
  }

  /**
   * Makes sure the bearer token was issued for role assignment `id`. The
   * pre-token Lambda writes the server's active assignment into the token
   * (`pnet:ra`) at sign-in and at every refresh, and the gateway and the
   * identity service compare it with the tab's X-PNET-Expected-Assignment
   * (409 `role_changed` when they differ). So once an assignment was made
   * active on the server, the token is refreshed ONCE before the first
   * request of that role.
   *
   * - the token already carries `id`: nothing is done (no network);
   * - another id, or none: one refresh (the strategy of the configured token kind);
   * - still no `pnet:ra` after the refresh (a Lambda of an older release): fine,
   *   the servers then compare with the active assignment in the database.
   *
   * Bounded (TOKEN_FOR_ASSIGNMENT_TIMEOUT_MS), never throws; skipped in mock mode.
   */
  static async ensureTokenForAssignment(id: number): Promise<void> {
    if (isMockAuthEnabled) return;
    const expected = String(id);
    const run = async () => {
      const current = await ApiProvider.getAuthToken();
      const carried = ApiProvider.tokenAssignmentId(current);
      if (carried === expected) return;
      ApiProvider.authDebug('ensureTokenForAssignment(): refreshing for the new assignment', {
        expected,
        carried,
      });
      const refreshed = await ApiProvider.refreshAuthToken();
      ApiProvider.authDebug('ensureTokenForAssignment(): refresh completed', {
        expected,
        refreshed: Boolean(refreshed),
        carried: ApiProvider.tokenAssignmentId(refreshed),
      });
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        run(),
        new Promise<void>((resolve) => {
          timer = setTimeout(() => {
            ApiProvider.authDebug('ensureTokenForAssignment(): timed out', { expected });
            resolve();
          }, ApiProvider.TOKEN_FOR_ASSIGNMENT_TIMEOUT_MS);
        }),
      ]);
    } catch (error: any) {
      ApiProvider.authDebug('ensureTokenForAssignment(): failed', {
        expected,
        name: error?.name,
        message: error?.message,
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private static async resolveAuthToken(): Promise<string | null> {
    // In local mock mode, there is no Cognito session to fall back to.
    if (isMockAuthEnabled) {
      ApiProvider.authDebug('resolveAuthToken(): mock mode fallback to localStorage token');
      return readAccessTokenCopy();
    }

    // Bearer token for the configured kind (IdToken in `id` mode, AccessToken
    // in `access` mode). See src/config/authToken.ts.
    let sessionToken: string | undefined;

    // Step 1: plain Amplify session read (uses cached tokens; does NOT call Cognito).
    try {
      const session = await fetchAuthSession();
      sessionToken = selectSessionToken(session.tokens);
      ApiProvider.authDebug('resolveAuthToken(): fetchAuthSession() succeeded', {
        session: ApiProvider.amplifySessionDebugMeta(session),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
    } catch (error: any) {
      ApiProvider.authDebug('resolveAuthToken(): fetchAuthSession() threw', {
        name: error?.name,
        message: error?.message,
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
    }

    // Step 2: if the token is missing or near-expiry, refresh it via
    // refreshAuthToken(), which picks the strategy for the configured kind:
    //
    //  - `id` mode: InitiateAuth REFRESH_TOKEN_AUTH (the classic Cognito API that
    //    always returns IdToken). WHY NOT fetchAuthSession({ forceRefresh: true })?
    //    Amplify v6.10+ switched its internal refresh path to the newer
    //    GetTokensFromRefreshToken Cognito API. That API does NOT return an IdToken
    //    for native (username/password) auth flows. Amplify's storeTokens() then
    //    deletes the stored IdToken from storage, making every subsequent session
    //    read return no IdToken — effectively logging the user out after 15 minutes.
    //  - `access` mode: Amplify's forceRefresh keeps the access token, so it is
    //    used first, with InitiateAuth as fallback.
    //
    // 300s (5 min) headroom: refresh starts well before expiry so a failed
    // attempt still leaves time for the retry inside refreshIdTokenViaInitiateAuth
    // to succeed before the user-visible request actually needs the token.
    if (!sessionToken || ApiProvider.isJwtExpiredOrNearExpiry(sessionToken, 300)) {
      ApiProvider.authDebug('resolveAuthToken(): token absent/near-expiry — refreshing', {
        tokenKind: authTokenKind,
        currentToken: ApiProvider.tokenDebugMeta(sessionToken),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      const refreshed = await ApiProvider.refreshAuthToken();
      if (refreshed) {
        sessionToken = refreshed;
        ApiProvider.authDebug('resolveAuthToken(): refresh succeeded', {
          sessionToken: ApiProvider.tokenDebugMeta(sessionToken),
        });
      } else {
        // Refresh unavailable (e.g. refresh token missing from storage, or a
        // network error). Fall through; the current token (if still valid) or
        // the stored-copy fallback will be returned below.
        ApiProvider.authDebug('resolveAuthToken(): refresh returned null', {
          storage: ApiProvider.amplifyStorageDebugMeta(),
        });
      }
    }

    if (sessionToken && !ApiProvider.isJwtExpiredOrNearExpiry(sessionToken, 5)) {
      // During fast account switching (e.g. SSO logout -> password login), Amplify
      // can briefly return a stale token from the previous user. If localStorage
      // already contains a newly established app session for a different user,
      // prefer the stored access token — but only if it is still valid.
      const localUser = ApiProvider.returnUserDataSafe();
      const localStoredToken = readAccessTokenCopy();
      const hasUsableLocalStoredToken = Boolean(
        localStoredToken && !ApiProvider.isJwtExpiredOrNearExpiry(localStoredToken, 5),
      );
      const amplifyTokenMatchesLocalUser = localUser
        ? ApiProvider.tokenMatchesUser(sessionToken, localUser)
        : true;
      const localStoredTokenMatchesLocalUser =
        localUser && localStoredToken
          ? ApiProvider.tokenMatchesUser(localStoredToken, localUser)
          : false;

      if (localUser && hasUsableLocalStoredToken && !amplifyTokenMatchesLocalUser) {
        if (localStoredTokenMatchesLocalUser) {
          ApiProvider.authDebug(
            'resolveAuthToken(): using stored token copy due to cross-user mismatch guard',
            {
              amplifyToken: ApiProvider.tokenDebugMeta(sessionToken),
              localStoredToken: ApiProvider.tokenDebugMeta(localStoredToken),
              amplifyTokenMatchesLocalUser,
              localStoredTokenMatchesLocalUser,
              localUserId: localUser?.id ?? null,
              localUserEmail: localUser?.email ?? null,
            },
          );
          return localStoredToken as string;
        }

        ApiProvider.authDebug(
          'resolveAuthToken(): both Amplify/local tokens mismatch current user; keeping Amplify token',
          {
            amplifyToken: ApiProvider.tokenDebugMeta(sessionToken),
            localStoredToken: ApiProvider.tokenDebugMeta(localStoredToken),
            amplifyTokenMatchesLocalUser,
            localStoredTokenMatchesLocalUser,
            localUserId: localUser?.id ?? null,
            localUserEmail: localUser?.email ?? null,
          },
        );
      }

      // Keep the in-memory copy in sync with the fresh token.
      try {
        storeAccessTokenCopy(sessionToken);
      } catch {
        // Ignore storage errors (e.g. private-browsing quota limits).
      }

      ApiProvider.authDebug('resolveAuthToken(): returning valid token', {
        sessionToken: ApiProvider.tokenDebugMeta(sessionToken),
      });
      return sessionToken;
    }

    // Last resort: a stored token may still be within its validity window.
    const storedToken = readAccessTokenCopy();
    if (ApiProvider.isJwtExpiredOrNearExpiry(storedToken, 5)) {
      ApiProvider.authDebug('resolveAuthToken(): no valid token after all attempts', {
        storedToken: ApiProvider.tokenDebugMeta(storedToken),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      return null;
    }
    ApiProvider.authDebug('resolveAuthToken(): falling back to the stored token copy', {
      storedToken: ApiProvider.tokenDebugMeta(storedToken),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    return storedToken;
  }

  /** The signed-in user of the in-memory session (auth/session.ts), or null. */
  private static returnUserDataSafe(): any | null {
    try {
      const userData = getSessionUser();
      return userData ? withActiveContextType(userData) : null;
    } catch {
      return null;
    }
  }

  private static decodeJwtPayloadSafe(token?: string | null): Record<string, any> | null {
    if (!token || typeof token !== 'string') return null;

    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded));

      return payload && typeof payload === 'object' ? payload : null;
    } catch {
      return null;
    }
  }

  private static isJwtExpiredOrNearExpiry(token?: string | null, skewSeconds = 30): boolean {
    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    if (!payload) return false;

    const exp = Number(payload.exp ?? NaN);
    if (!Number.isFinite(exp) || exp <= 0) {
      return false;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp <= nowSeconds + Math.max(0, skewSeconds);
  }

  /**
   * Does this Cognito JWT belong to the locally signed-in app user?
   *
   * Works for both token kinds:
   *  - `sub` exists on ID and access tokens and is compared with the backend's
   *    `users.cognito_sub` when the login response carried it.
   *  - `email` exists on ID tokens only. Access tokens carry `username`
   *    (`cognito:username` on ID tokens); federated usernames look like
   *    `<idp>_<email>`, hence the endsWith check.
   */
  private static tokenMatchesUser(token: string, user: any): boolean {
    if (!token || !user) return false;

    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    if (!payload) return false;

    const tokenSub = String(payload.sub ?? '').trim();
    const userSub = String(user?.cognito_sub ?? '').trim();
    if (tokenSub && userSub) {
      return tokenSub === userSub;
    }

    const tokenEmail = String(payload.email ?? payload['custom:email'] ?? '')
      .trim()
      .toLowerCase();
    const userEmail = String(user?.email ?? '')
      .trim()
      .toLowerCase();
    if (tokenEmail && userEmail) {
      return tokenEmail === userEmail;
    }

    const tokenUsername = String(payload['cognito:username'] ?? payload.username ?? '')
      .trim()
      .toLowerCase();
    if (tokenUsername && userEmail) {
      return tokenUsername === userEmail || tokenUsername.endsWith(`_${userEmail}`);
    }

    return false;
  }

  private static isUnauthorizedResponse(response: any, httpStatus?: number): boolean {
    if (httpStatus === 401) return true;

    if (!response) return false;

    if (typeof response === 'string') {
      return response.trim().toLowerCase() === 'unauthorized';
    }

    const message = String(response.message ?? response.error ?? '')
      .trim()
      .toLowerCase();
    if (message === 'unauthorized') return true;

    const statusCode = Number(response.status_code ?? response.status ?? NaN);
    return Number.isFinite(statusCode) && statusCode === 401;
  }

  private static isAbortError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      (error as { name?: unknown }).name === 'AbortError'
    );
  }

  private static async parseResponsePayload(response: Response): Promise<any> {
    if (response.status === 204) {
      return { success: true };
    }

    const responseClone = response.clone();

    try {
      return await response.json();
    } catch (error: unknown) {
      // The request was cancelled while its body was being read (the user left the page
      // mid-load). There is no data, so this must never look like a success to the caller.
      if (ApiProvider.isAbortError(error)) {
        return { success: false, aborted: true };
      }

      if (response.status === 401) {
        return { success: false, message: 'Unauthorized' };
      }

      let text = '';
      try {
        text = await responseClone.text();
      } catch {
        text = '';
      }

      return {
        success: response.ok,
        message: text || response.statusText || 'Request failed',
        status: response.status,
      };
    }
  }

  private static async parseAndHandleAuthenticatedResponse(
    response: Response,
    options: AuthenticatedFetchOptions = {},
  ): Promise<any> {
    const payload = await ApiProvider.parseResponsePayload(response);

    if (response.status === 401 || ApiProvider.isUnauthorizedResponse(payload, response.status)) {
      ApiProvider.authDebug(
        'parseAndHandleAuthenticatedResponse(): unauthorized response received',
        {
          requestLabel: options.requestLabel ?? null,
          httpStatus: response.status,
          payloadMessage:
            typeof payload === 'object' ? (payload?.message ?? payload?.error ?? null) : payload,
          skipUnauthorizedHandling: options.skipUnauthorizedHandling === true,
          forcedRefreshAttempted: options.forcedRefreshAttempted === true,
        },
      );
    }

    if (!options.skipUnauthorizedHandling && options.redirectOnUnauthorized === true) {
      // AE-I2596: never wipe local auth state + redirect on a 401 that arrived
      // BEFORE a forced refresh was attempted. A route-change or window-focus
      // probe can easily hit a stale/short-lived token or a transient backend
      // failure; wiping on that first 401 is what made users get logged out
      // continuously. Only a CONFIRMED failure — forced refresh was attempted
      // and either produced no usable token, or the retried request STILL came
      // back 401 — is treated as a genuinely dead session.
      if (options.forcedRefreshAttempted === true) {
        ApiProvider.handleUnauthorizedResponse(payload, response.status);
      } else {
        ApiProvider.authDebug(
          'parseAndHandleAuthenticatedResponse(): unauthorized but no forced refresh yet; deferring session wipe',
          {
            requestLabel: options.requestLabel ?? null,
            httpStatus: response.status,
          },
        );
      }
    }

    if (response.ok && !ApiProvider.isUnauthorizedResponse(payload, response.status)) {
      ApiProvider.lastSessionProbeAt = Date.now();
    }

    return payload;
  }

  private static async getForcedFreshAuthToken(): Promise<string | null> {
    if (isMockAuthEnabled) {
      ApiProvider.authDebug('getForcedFreshAuthToken(): mock mode fallback');
      return readAccessTokenCopy();
    }

    // Forced refresh for the configured token kind (see refreshAuthToken for
    // why `id` mode avoids fetchAuthSession({ forceRefresh: true })).
    ApiProvider.authDebug('getForcedFreshAuthToken(): attempting forced refresh', {
      tokenKind: authTokenKind,
      localStorageTokenBefore: ApiProvider.tokenDebugMeta(readAccessTokenCopy()),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    const refreshed = await ApiProvider.refreshAuthToken();
    if (refreshed && !ApiProvider.isJwtExpiredOrNearExpiry(refreshed, 5)) {
      ApiProvider.authDebug('getForcedFreshAuthToken(): forced refresh returned valid token', {
        token: ApiProvider.tokenDebugMeta(refreshed),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      return refreshed;
    }

    ApiProvider.authDebug('getForcedFreshAuthToken(): forced refresh returned null/expired', {
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });

    const storedToken = readAccessTokenCopy();
    if (ApiProvider.isJwtExpiredOrNearExpiry(storedToken, 5)) {
      ApiProvider.authDebug('getForcedFreshAuthToken(): no valid fallback token', {
        storedToken: ApiProvider.tokenDebugMeta(storedToken),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      return null;
    }
    ApiProvider.authDebug('getForcedFreshAuthToken(): using the stored token copy', {
      storedToken: ApiProvider.tokenDebugMeta(storedToken),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    return storedToken;
  }

  /**
   * Shared flow for every authenticated request: the `token` handed to
   * `requestFactory` is the bearer for the configured kind (IdToken by default,
   * AccessToken when VITE_AUTH_TOKEN_KIND=access), so the per-endpoint helpers
   * below need no knowledge of the token model.
   */
  private static async fetchAuthenticatedWithSingleRetry(
    requestFactory: (token: string) => Promise<Response>,
    options: AuthenticatedFetchOptions = {},
  ): Promise<any> {
    let token = await ApiProvider.getAuthToken();
    if (!token) {
      // Right after login, concurrent session probes can race before the token
      // lands in storage. Retry briefly to avoid false unauthorized responses.
      for (const delayMs of [250, 500]) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        token = await ApiProvider.getAuthToken();
        if (token) break;
      }
    }

    if (!token) {
      // AE-I2596: a missing Amplify token is NOT proof the session is dead (the
      // Cognito session can briefly race login/SSO). Try a FORCED refresh before
      // declaring failure. handleUnauthorizedResponse() still guards on
      // a session hint being present, so a genuinely anonymous probe never wipes.
      const forcedToken = await ApiProvider.getForcedFreshAuthToken();
      if (forcedToken && !ApiProvider.isJwtExpiredOrNearExpiry(forcedToken, 5)) {
        ApiProvider.authDebug(
          'fetchAuthenticatedWithSingleRetry(): no cached token; forced refresh produced usable token',
          {
            requestLabel: options.requestLabel ?? null,
            token: ApiProvider.tokenDebugMeta(forcedToken),
          },
        );
        token = forcedToken;
      } else {
        ApiProvider.authDebug(
          'fetchAuthenticatedWithSingleRetry(): no token available after forced refresh',
          {
            requestLabel: options.requestLabel ?? null,
          },
        );
        if (options.redirectOnUnauthorized === true) {
          ApiProvider.handleUnauthorizedResponse({ message: 'Unauthorized' }, 401);
        }
        return { success: false, message: 'Unauthorized', status: 401 };
      }
    }

    ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): sending initial request', {
      requestLabel: options.requestLabel ?? null,
      token: ApiProvider.tokenDebugMeta(token),
    });
    let response = await requestFactory(token);
    ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): initial response received', {
      requestLabel: options.requestLabel ?? null,
      httpStatus: response.status,
    });

    let forcedRefreshAttempted = false;

    if (response.status === 401) {
      forcedRefreshAttempted = true;
      ApiProvider.authDebug(
        'fetchAuthenticatedWithSingleRetry(): initial 401, trying forced refresh',
        {
          requestLabel: options.requestLabel ?? null,
          originalToken: ApiProvider.tokenDebugMeta(token),
        },
      );
      const retryToken = await ApiProvider.getForcedFreshAuthToken();
      const canRetry =
        !!retryToken && (retryToken !== token || ApiProvider.isJwtExpiredOrNearExpiry(token, 5));

      ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): retry decision', {
        requestLabel: options.requestLabel ?? null,
        canRetry,
        retryToken: ApiProvider.tokenDebugMeta(retryToken),
        originalTokenNearExpiry: ApiProvider.isJwtExpiredOrNearExpiry(token, 5),
        tokenChanged: retryToken ? retryToken !== token : false,
      });
      if (canRetry) {
        response = await requestFactory(retryToken as string);
        ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): retry response received', {
          requestLabel: options.requestLabel ?? null,
          httpStatus: response.status,
        });
      }
    }

    return ApiProvider.parseAndHandleAuthenticatedResponse(response, {
      ...options,
      forcedRefreshAttempted,
    });
  }

  /**
   * Headers of every authenticated request to the gateway and the identity
   * service: the bearer token, the legacy `UserId` (the gateway overwrites it
   * from the token) and `X-PNET-Expected-Assignment`, the role assignment this
   * tab was loaded with (Authorization-Plan §13.6; a mismatch answers 409
   * `role_changed`, see auth/roleChange.ts).
   */
  static buildAuthHeaders(
    token: string | null,
    extra: Record<string, string> = {},
  ): Record<string, string> {
    const headers: Record<string, string> = { ...extra };
    if (token) headers.Authorization = `Bearer ${token}`;
    const userData = ApiProvider.returnUserDataSafe();
    if (userData?.id) headers.UserId = String(userData.id);
    return { ...headers, ...expectedAssignmentHeaders() };
  }

  static async ensureSessionAlive(force = false): Promise<boolean> {
    if (ApiProvider.isSessionRedirectInProgress) {
      return false;
    }

    const hasUserData = isSignedIn();
    const hasAccessToken = hasSessionHint();
    if (!hasUserData || !hasAccessToken) {
      return false;
    }

    // identity's me/details answers for the ACTIVE role only: a signed-in user
    // without a confirmed role in this tab (no valid role -> the no-access
    // page, only a role of another application, or the page-load role check
    // still running) would get 403 on every page. Nothing to probe then;
    // RoleGate's role check (me/assignments) covers that state. Without an
    // identity URL (local mock setups) the probe is the gateway's per-user
    // User/Details, as before.
    if (identityApiUrl && !getTabAssignment()) {
      return true;
    }

    const now = Date.now();
    if (!force && now - ApiProvider.lastSessionProbeAt < ApiProvider.SESSION_PROBE_INTERVAL_MS) {
      return true;
    }

    if (ApiProvider.sessionProbeInFlight) {
      return ApiProvider.sessionProbeInFlight;
    }

    ApiProvider.sessionProbeInFlight = (async () => {
      try {
        const payload = await ApiProvider.fetchAuthenticatedWithSingleRetry(
          async (token) =>
            roleAwareFetch(ApiProvider.userDetailsUrl(), {
              method: 'GET',
              headers: ApiProvider.buildAuthHeaders(token, {
                'content-type': 'application/json',
                accept: 'application/json',
              }),
            }),
          { requestLabel: 'me/details (session probe)', redirectOnUnauthorized: true },
        );

        if (ApiProvider.isUnauthorizedResponse(payload)) {
          ApiProvider.authDebug(
            'ensureSessionAlive(): backend reported unauthorized after retry flow',
            {
              payloadMessage:
                typeof payload === 'object'
                  ? (payload?.message ?? payload?.error ?? null)
                  : payload,
            },
          );
          return false;
        }

        // Treat non-auth failures (network/server) as "unknown" and avoid
        // force-logging out the user. The next API call or probe can retry.
        return true;
      } catch (error: any) {
        ApiProvider.authDebug(
          'ensureSessionAlive(): probe threw non-auth error (treated as keep-session)',
          {
            name: error?.name,
            message: error?.message,
          },
        );
        return true;
      } finally {
        ApiProvider.sessionProbeInFlight = null;
      }
    })();

    return ApiProvider.sessionProbeInFlight;
  }

  static webservicePost(pageName: string, postParam: BodyInit | null): Promise<any> {
    return new Promise((resolve) => {
      fetch(this.apiUrl + pageName, {
        method: 'POST',
        body: postParam,
      })
        .then((response) => response.json())
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenDelete(
    pageName: string,
    postParam: BodyInit | null,
  ): Promise<any> {
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) =>
          roleAwareFetch(this.apiUrl + pageName, {
            method: 'DELETE',
            headers: ApiProvider.buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
            body: JSON.stringify(postParam),
          }),
        { requestLabel: `DELETE ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostToken(
    pageName: string,
    postParam: BodyInit | null,
    loader: 'yes' | 'no' | null,
    signal: AbortSignal | null = null,
    options: { suppressUnauthorizedRedirect?: boolean } = {},
  ): Promise<any> {
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          // Set Content-Type for JSON requests (string body)
          const headers = ApiProvider.buildAuthHeaders(
            token,
            typeof postParam === 'string' ? { 'Content-Type': 'application/json' } : {},
          );

          return roleAwareFetch(this.apiUrl + pageName, {
            method: 'POST',
            headers,
            body: postParam,
            signal: signal,
          });
        },
        {
          requestLabel: `POST ${pageName}`,
          skipUnauthorizedHandling: options.suppressUnauthorizedRedirect === true,
        },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenPut(
    pageName: string,
    postParam: BodyInit | object | null,
  ): Promise<any> {
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers = ApiProvider.buildAuthHeaders(token, {
            'Content-Type': 'application/json',
          });

          return roleAwareFetch(this.apiUrl + pageName, {
            method: 'PUT',
            headers,
            body: JSON.stringify(postParam),
          });
        },
        { requestLabel: `PUT ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenPatch(
    pageName: string,
    postParam: BodyInit | object | null,
  ): Promise<any> {
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers = ApiProvider.buildAuthHeaders(token, {
            'Content-Type': 'application/json',
          });

          return roleAwareFetch(this.apiUrl + pageName, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(postParam),
          });
        },
        { requestLabel: `PATCH ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webserviceGet(
    pageName: string,
    queryParams?: string | string[][] | Record<string, string> | URLSearchParams | null,
    signal: AbortSignal | null = null,
  ): Promise<any> {
    let query: string = '';
    if (queryParams) {
      // Append the encoded string as-is. This used to be wrapped in
      // decodeURIComponent, which turned %26 / %3D inside a *value* back into
      // real & and = delimiters, so a user-typed value broke out and became
      // extra query params: searching for "x&limit=9999" sent a second `limit`
      // and defeated the caller's page cap. It also turned an encoded plus
      // (%2B) into a literal +, which servers read back as a space. The other
      // query builders in this file already append the encoded string directly
      // against the same backends.
      query = '?' + new URLSearchParams(queryParams).toString();
    }

    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) =>
          roleAwareFetch(this.apiUrl + pageName + query, {
            method: 'GET',
            headers: ApiProvider.buildAuthHeaders(token, {
              'content-type': 'application/json',
              accept: 'application/json',
            }),
            signal: signal,
          }),
        { requestLabel: `GET ${pageName}${query}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  /**
   * Called after parsing every authenticated API response.
   * If the server reports "Unauthorized" (expired/invalid/revoked JWT), clear
   * local auth state and hard-navigate to the login page so the user isn't
   * silently stuck on a broken page. We guard on a session hint (hasSessionHint) being present
   * so unauthenticated flows (login page itself) are never accidentally
   * redirected.
   */
  private static handleUnauthorizedResponse(response: any, httpStatus?: number): void {
    if (ApiProvider.isUnauthorizedResponse(response, httpStatus) && hasSessionHint()) {
      if (ApiProvider.isSessionRedirectInProgress) {
        ApiProvider.authDebug(
          'handleUnauthorizedResponse(): redirect already in progress, ignoring duplicate unauthorized',
        );
        return;
      }
      ApiProvider.authDebug('handleUnauthorizedResponse(): triggering session-expired redirect', {
        httpStatus: httpStatus ?? null,
        payloadMessage:
          typeof response === 'object'
            ? (response?.message ?? response?.error ?? null)
            : String(response),
        currentPath:
          typeof window !== 'undefined' ? window.location.pathname + window.location.hash : null,
        localStorageToken: ApiProvider.tokenDebugMeta(readAccessTokenCopy()),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      ApiProvider.isSessionRedirectInProgress = true;

      try {
        sessionStorage.setItem(
          ApiProvider.SESSION_EXPIRED_MESSAGE_KEY,
          ApiProvider.SESSION_EXPIRED_MESSAGE_TEXT,
        );
      } catch {
        // Ignore storage failures; redirect should still proceed.
      }

      // Session and role state (keeps `sessionurl`, so the next sign-in
      // returns to the page; see auth/clearSession.ts). The session is in
      // memory and components follow it, so they may re-render without a user
      // in this moment: as during a sign-out, the error screens stay blank
      // until the page load below.
      markSigningOut();
      clearSession('expired');
      window.location.replace('/');
    }
  }

  static consumeSessionExpiredMessage(): string | null {
    try {
      const message = sessionStorage.getItem(ApiProvider.SESSION_EXPIRED_MESSAGE_KEY);
      if (message) {
        sessionStorage.removeItem(ApiProvider.SESSION_EXPIRED_MESSAGE_KEY);
      }
      return message;
    } catch {
      return null;
    }
  }

  /** Strip HTML tags from backend-supplied strings before displaying */
  private static sanitizeMessage(message: string): string {
    if (typeof message !== 'string') return 'An error occurred';
    // Remove HTML tags to prevent any accidental HTML injection via toast
    return (
      message
        .replace(/<[^>]*>/g, '')
        .trim()
        .slice(0, 500) || 'An error occurred'
    );
  }

  static globalToastMessage(
    message: string,
    duration: number = 5000,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
  ) {
    const safeMessage = ApiProvider.sanitizeMessage(message);
    const configs: ToastOptions = {
      position: 'top-right',
      autoClose: duration,
      theme: 'colored',
    };

    if (type == 'success') {
      toast.success(safeMessage, configs);
    } else if (type == 'error') {
      toast.error(safeMessage, configs);
    } else if (type == 'warning') {
      toast.warning(safeMessage, configs);
    } else {
      toast.info(safeMessage, configs);
    }
  }

  static globalApiErrorMessage(message: ApiErrorMessage): void {
    if (ApiProvider.isUnauthorizedResponse(message)) {
      // Unauthorized is handled centrally with a forced redirect and a single
      // post-redirect message on the login page.
      return;
    }
    if (isRoleChangedPayload({ message })) {
      // 409 role_changed: the blocking banner (RoleChangedBanner) says it.
      return;
    }

    if (typeof message === 'string') {
      if (message === 'Unexpected token < in JSON at position 0') {
        this.globalToastMessage('Something wrong. Please try again', 5000, 'error');
      } else {
        this.globalToastMessage(message, 5000, 'error');
      }
    } else {
      for (const key in message) {
        this.globalToastMessage(message[key][0], 5000, 'error');
      }
    }
  }

  /**
   * The signed-in user. `type` is the ACTIVE role assignment's context type in
   * the legacy codes (INTERNAL 1, BRAND 2, RIGHT_HOLDER 3, AGENCY 4), the
   * account's own type is `account_type` (auth/activeAssignment.ts).
   */
  static returnUserData(): any {
    return withActiveContextType(getSessionUser());
  }

  /** The landing modules of the active role (identity me/details), or null. */
  static returnUserAccessData(): any {
    return getSessionModules();
  }

  /**
   * The header page context of the e-mail template pages, by the ACTIVE
   * context type (part 7): right holder -> `property_id`, brand / agency ->
   * `brand_id`, internal / none -> nothing.
   */
  static returnUserBrandOrPropertyId() {
    let brand_id = '';
    let property_id = '';
    const contextType = getActiveContextType();
    if (contextType === 'RIGHT_HOLDER') {
      property_id = getHeaderPropertyId() || '';
    }
    if (contextType === 'AGENCY' || contextType === 'BRAND') {
      brand_id = getHeaderBrandId() || '';
    }

    return removeEmpty({
      brand_id,
      property_id,
    });
  }

  static redirectToPermissionDeniedPage() {
    const newHref = window.location.href.split('#');
    window.location.href = newHref[0] + '#/nopermission';
  }

  static displayDateFormat(date: string) {
    if (date == '' || date == null || date == undefined) {
      return '';
    }
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'June',
      'July',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];
    const d = new Date(date);
    const month = months[d.getMonth()];
    const getDate = d.getDate();
    const getFullYear = d.getFullYear();
    return getDate + ' ' + month + ' ' + getFullYear;
  }

  static downloadFile(
    document_media_id: number,
    media_id: number,
    module_type: string,
    displayType: string,
  ) {
    const body = new FormData();
    if (module_type == 'document') {
      body.append('document_media_id', String(document_media_id));
    } else {
      body.append('media_ids[]', String(media_id));
    }
    body.append('download_type', module_type);
    body.append('local_path', 'true');
    this.webservicePostToken('DocumentMedia/DownloadDocumentMedia', body, 'yes').then(
      (response: any) => {
        if (response.success == true) {
          const baseurl = this.apiUrl.split('/mservices/');
          const fileUrl =
            baseurl[0] + '/mservices/contract/storage/app/' + response.data.local_path;
          if (displayType == 'view') {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
          } else {
            new JsFileDownloader({
              url: fileUrl,
            })
              .then(function () {
                // Called when download ended
              })
              .catch(function () {
                // Called when an error occurred
              });
          }
        } else {
          this.globalApiErrorMessage(response.message);
        }
      },
      (error) => {
        this.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  // Currency functions start
  static filterCurrency() {
    let currencyString = '';

    const localStorageFilters: { filter_type: string }[] = getHeaderFilters() ?? [];

    if (localStorageFilters) {
      const currencyItem = localStorageFilters.find(
        (item) => item.filter_type == 'currency',
      ) as GlobalCurrencyFilterDTO;
      currencyString = currencyItem
        ? currencyItem.currency_code
        : this.getCurrencyValueFromCommonData();
    }

    if (!currencyString) {
      currencyString = this.returnUserData()?.currency?.currency_code;
    }

    return currencyString;
  }

  static getCurrencyValueFromCommonData() {
    const commonData = ApiProvider.getMasterData();
    if (commonData && commonData.comman.currency) {
      const currency_id = ApiProvider.returnUserData()?.currency_id;
      const currencyItem = commonData.comman.currency.find((item: any) => item.id == currency_id);
      return currencyItem ? currencyItem.currency_code : '';
    }
    return '';
  }

  static userCurrency() {
    let currencyValue = '';
    const commonData = ApiProvider.getMasterData();
    if (commonData != '') {
      if (commonData.comman.currency) {
        const currency = commonData.comman.currency;

        const currency_id = ApiProvider.returnUserData()?.currency_id;
        let filteredItens = currency;
        filteredItens = filteredItens.filter((item: any) => {
          return item.id == currency_id;
        });
        if (filteredItens.length > 0) {
          currencyValue = filteredItens[0].currency_code + ' (' + filteredItens[0].symbol + ')';
        }
      }
    }
    return currencyValue;
  }

  static currencyConvertor(val: number) {
    if (!val && val !== 0) return ''; // Return empty string for invalid or undefined values
    const hasDecimals = val % 1 !== 0; // Check if there are decimals
    return hasDecimals ? val.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : val.toLocaleString();
  }

  // Currency functions end

  // Get Master Common Data start
  static async getMasterCommonData(modulename: string) {
    const parameters: Record<string, string> = { flag: 'active' };
    if (modulename != '') {
      parameters.module_type = modulename;
    }

    const response = (await this.webserviceGet(
      'GetMasterCommanData',
      parameters,
    )) as ResponseDTO<any>;

    if (!response || !response.success) return;

    setSessionCommonData(response.data);
  }

  /** The master data of the forms (in memory, loaded for the active role), or ''. */
  static getMasterData(): any {
    return getSessionCommonData() || '';
  }

  static exchange_rates_text() {
    return 'Please contact system administrator to add the exchange rates';
  }

  /** GET User/PermissionsMap as answered (`{}` when the role holds nothing). */
  static async requestPermissionsMap(): Promise<Record<string, boolean>> {
    const response = await ApiProvider.webserviceGet('User/PermissionsMap');
    if (!response.success) throw new Error(response.message);
    const map = response.data;
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
  }

  /**
   * No role is confirmed in this tab (no valid role: the no-access page; or the
   * role check could not answer and the login named no current role). The
   * role's data (User/PermissionsMap, me/details, master data, ...) is answered
   * for the ACTIVE role only, so it would be refused (403, page sweep #34):
   * nothing is asked then, as for the session probe (C2). Without an identity
   * URL (local mock setups) the role data is the gateway's per-user data, as before.
   */
  static withoutActiveRole(): boolean {
    return Boolean(identityApiUrl) && !getTabAssignment();
  }

  // Permission
  static async fetchPermissions() {
    if (ApiProvider.withoutActiveRole()) return;
    try {
      // The permissions the ACTIVE role assignment holds (Authorization-Plan
      // §6.2 step 5): identity answers only those (name: true; identity part
      // 7l, a name not held is absent), from the active effective access
      // (unit identity_user_roles `new`). me/effective-access is for access
      // administrators only and is not asked here.
      // Kept in memory only (auth/session.ts), under the active assignment's key.
      await queryClient.fetchQuery({
        queryKey: roleSessionKeys.permissions(),
        queryFn: ApiProvider.requestPermissionsMap,
        staleTime: 0,
        gcTime: SESSION_GC_TIME,
        retry: false,
      });
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  // Get Media Value Type
  static async getMediaValueType() {
    try {
      const response = await ApiProvider.webserviceGet('Activation/User/GetMediaValueType');
      if (!response.success) throw new Error(response.message);
      localStorage.setItem('mediaValueTypeList', JSON.stringify(response.data));
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  static permission(permissionName: string): boolean {
    try {
      // a permission not held is absent from the map (identity part 7l); the
      // map is the active role's, in memory (auth/session.ts)
      return sessionHasPermission(permissionName);
    } catch (error: any) {
      console.error(error.message);

      return false;
    }
  }

  static getRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  static getRandomColorByIndex(index: number) {
    const colorArray = [
      '#004D79',
      '#C66FBD',
      '#007969',
      '#E69400',
      '#79004D',
      '#7979C3',
      '#FF7A5B',
      '#2C0079',
      '#3679AB',
      '#CFB86A',
      '#677D92',
    ];
    let colorCode = '';
    if (colorArray[index]) {
      colorCode = colorArray[index];
    } else {
      colorCode = ApiProvider.getRandomColor();
    }
    return colorCode;
  }

  static returnMediaValueList() {
    try {
      const data = localStorage.getItem('mediaValueTypeList');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /* ------------------ AUTHENTICATION ------------------ */

  /**
   * PNET's sign-in sequence after LoginData. `options.resolveAssignment` runs
   * once the session token is stored and before any role-dependent data is
   * loaded: it chooses the active role assignment (Authorization-Plan §6.2:
   * 0 valid -> NoAccessError, 1 -> selected, several -> selection screen;
   * auth/roleSelection.ts) and answers false when the user has no access.
   */
  static async signInUserWithUserData(
    userData: any,
    message: string,
    setHeaderBrand: any,
    setHeaderProperty: any,
    options: { resolveAssignment?: () => Promise<boolean> } = {},
  ) {
    // The session lives in memory (auth/session.ts); `type` follows the
    // active assignment on every read (no users.type decisions).
    setSessionUser(userData.user);
    writeSessionData(sessionKeys.loginContext, userData.active_context ?? null);

    let resolvedAccessToken =
      typeof userData?.access_token === 'string' ? userData.access_token.trim() : '';
    const hasResponseToken =
      resolvedAccessToken !== '' &&
      resolvedAccessToken !== 'undefined' &&
      resolvedAccessToken !== 'null';

    // Cognito token for the configured kind (IdToken in `id` mode, AccessToken in
    // `access` mode); the backend echoes the same token back as `access_token`.
    let cognitoSessionToken = '';
    // In mock mode there is no real Cognito session — skip fetchAuthSession() entirely
    // so a stale Cognito token never replaces the mock JWT stored by the backend.
    if (!isMockAuthEnabled) {
      try {
        const session = await fetchAuthSession();
        cognitoSessionToken = selectSessionToken(session.tokens) || '';
      } catch {
        // Ignore session-read failures. We'll fall back to the login response token.
      }
    }

    if (cognitoSessionToken && !ApiProvider.isJwtExpiredOrNearExpiry(cognitoSessionToken, 5)) {
      const responseTokenMatchesUser =
        hasResponseToken && userData?.user
          ? ApiProvider.tokenMatchesUser(resolvedAccessToken, userData.user)
          : false;
      const cognitoTokenMatchesUser = userData?.user
        ? ApiProvider.tokenMatchesUser(cognitoSessionToken, userData.user)
        : true;

      // Prefer the active Cognito token when response token is missing/invalid
      // or appears tied to a different user during fast account switching.
      if ((!hasResponseToken || !responseTokenMatchesUser) && cognitoTokenMatchesUser) {
        resolvedAccessToken = cognitoSessionToken;
      } else if (!hasResponseToken || !responseTokenMatchesUser) {
        ApiProvider.authDebug(
          'signInUserWithUserData(): keeping response token because Amplify token mismatched user',
          {
            responseToken: ApiProvider.tokenDebugMeta(
              hasResponseToken ? resolvedAccessToken : undefined,
            ),
            cognitoSessionToken: ApiProvider.tokenDebugMeta(cognitoSessionToken),
            responseTokenMatchesUser,
            cognitoTokenMatchesUser,
            userId: userData?.user?.id ?? null,
            userEmail: userData?.user?.email ?? null,
          },
        );
      }
    }

    if (
      !resolvedAccessToken ||
      resolvedAccessToken === 'undefined' ||
      resolvedAccessToken === 'null'
    ) {
      throw new Error('Unable to establish authenticated session token');
    }

    storeAccessTokenCopy(resolvedAccessToken);
    // Never persist a refresh token here: Amplify (local mode) or the identity
    // service's httpOnly cookie (cookie mode) owns it, and nothing reads this key.
    localStorage.removeItem('refresh_token');
    localStorage.setItem('expires_in', userData.expires_in);
    // The user's default saved filter: the header starts from it when the server
    // keeps no selection for the role (loadHeaderSelection below).
    writeSessionData(
      sessionKeys.defaultFilter,
      userData.default_filter?.[0]?.filters_comination ?? null,
    );

    if (options.resolveAssignment && !(await options.resolveAssignment())) {
      throw new NoAccessError();
    }

    // me/assignments unavailable: LoginData's `active_context` (the server's
    // current assignment) still gives the context type (part 7).
    if (!getTabAssignment()) {
      const fromLogin = assignmentFromLoginContext(
        userData.active_context,
        Number(userData.user?.id) || null,
      );
      if (fromLogin) setActiveAssignment(fromLogin);
    }

    // The role's last header selection (brand, partner, filter) from the server;
    // without a record the default saved filter. In memory only.
    await this.loadHeaderSelection();

    await this.loadRoleData(userData.user.id, setHeaderBrand, setHeaderProperty);

    //--login redirection with user permission
    if (message) {
      this.globalToastMessage(message, 5000, 'success');
    }

    return this.returnUserData();
  }

  /**
   * Role-dependent data of the ACTIVE role assignment: the session part kept
   * in memory (permissions map, landing modules, master data; auth/session.ts)
   * and the header brand / partner when the scope has exactly one. Runs at
   * sign-in and on a role switch (before the full reload). No user id is
   * sent: the token and its active assignment decide (part 7). `userId` is
   * kept for callers.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async loadRoleData(_userId: number, setHeaderBrand: any, setHeaderProperty: any) {
    if (ApiProvider.withoutActiveRole()) return;
    await this.fetchPermissions();

    await this.getMediaValueType();

    const modules = this.get_user_access();

    await Promise.all([
      this.getUserAccessBrand(setHeaderBrand),
      this.getMasterCommonData(''),
      modules,
    ]);

    if (getActiveContextType() === 'RIGHT_HOLDER') {
      await this.getUserAccessProperty(setHeaderProperty);
    }
  }

  /**
   * Hard refresh: the session part of the active role's data (permissions
   * map, landing modules, master data), when this page does not hold it yet.
   * The session is in memory only, so a page load starts without it
   * (auth/RoleGate.tsx runs this before a gated page renders). Nothing is
   * loaded again when a sign-in or role switch on this page already did.
   */
  static async loadRoleSession(): Promise<void> {
    if (roleSessionLoaded() || ApiProvider.withoutActiveRole()) return;
    await Promise.all([
      this.fetchPermissions(),
      this.get_user_access(),
      this.getMasterCommonData(''),
      // a RIGHT_HOLDER role's start partner (N2), for the header's home link
      this.loadHomePartnerInTime(),
    ]);
  }

  /**
   * The page load waits for the start partner, so the wait is bounded like the
   * header selection's and a failure never fails the page load. A late answer
   * is still kept (the home link reads it at its next render).
   */
  static async loadHomePartnerInTime(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      ApiProvider.loadHomePartner().catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ApiProvider.HEADER_SELECTION_LOAD_TIMEOUT_MS);
      }),
    ]);
    clearTimeout(timer);
  }

  /**
   * The header selection of the ACTIVE role assignment (header brand, header
   * partner, the global filter; selection/headerSelection.ts), from the
   * server into memory: it is not in browser storage, so a page load, a
   * sign-in and a role switch start without it (UI state plan, step A).
   * The server answers the user's last selection under this assignment,
   * narrowed to the assignment's scope. No record: the user's default saved
   * filter (LoginData `default_filter`), no header brand / partner (the
   * header selects the scope's only brand / partner itself). No answer: the
   * same, in memory only (nothing is saved over a record that was not read).
   * Nothing is loaded again when this page already holds it.
   *
   * The page load waits for it, so the wait is bounded
   * (HEADER_SELECTION_LOAD_TIMEOUT_MS): a store that does not answer in time
   * counts as "no answer", and its late answer is ignored.
   */
  static HEADER_SELECTION_LOAD_TIMEOUT_MS = 4000;

  static async loadHeaderSelection(): Promise<void> {
    const assignmentId = activeAssignmentId();
    if (isHeaderSelectionLoaded(assignmentId)) return;

    let answered = false;
    let stored: Partial<StoredHeaderSelection> | null = null;
    if (assignmentId !== null) {
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const response: any = await Promise.race([
        ApiProvider.webserviceGet('Dashboard/Filters/HeaderSelection', null, controller.signal),
        new Promise((resolve) => {
          timer = setTimeout(() => {
            controller.abort();
            resolve({ success: false, message: 'timeout' });
          }, ApiProvider.HEADER_SELECTION_LOAD_TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timer);
      // the role changed, or the user signed out, while the request ran
      if (activeAssignmentId() !== assignmentId || !isSignedIn()) return;
      if (isHeaderSelectionLoaded(assignmentId)) return;
      answered = response?.success === true;
      const selection = answered ? response.data?.selection : null;
      stored = selection && typeof selection === 'object' ? selection : null;
    }
    if (stored) {
      hydrateHeaderSelection(stored, assignmentId, true);
      return;
    }
    // what this page chose meanwhile stays; else the defaults
    const current = getHeaderSelection(assignmentId);
    const untouched = !current.brand && !current.property && current.filters === null;
    hydrateHeaderSelection(
      untouched
        ? { filters: parseHeaderFilters(readSessionData(sessionKeys.defaultFilter)) }
        : toStoredSelection(current),
      assignmentId,
      answered,
    );
  }

  /**
   * Keeps the header selection on the server for the user and the role
   * assignment it was made under (selection/headerSelection.ts sends every
   * change, debounced). Quiet: no toast, no sign-out on a refusal; a later
   * change is sent again. Nothing is sent while a role is previewed (the
   * preview is not the user's own work) or once the assignment changed.
   */
  static async saveHeaderSelection(
    selection: StoredHeaderSelection,
    assignmentId: number,
  ): Promise<boolean> {
    if (!isSignedIn() || activeAssignmentId() !== assignmentId) return false;
    if (getPreviewContextType() !== null) return false;
    const body = new FormData();
    // `<` escaped: the gateway strips tags from every text value (XssSanitizer)
    body.append('selection', JSON.stringify(selection).replace(/</g, '\\u003c'));
    body.append('assignment_id', String(assignmentId));
    const response: any = await ApiProvider.webservicePostToken(
      'Dashboard/Filters/HeaderSelection',
      body,
      'no',
      null,
      { suppressUnauthorizedRedirect: true },
    );
    return response?.success === true;
  }

  /**
   * Hard refresh: the signed-in user from the server, for a page that starts
   * with an empty in-memory session (auth/sessionBoot.ts).
   *
   * With an identity service (VITE_IDENTITY_API_URL) this is its read-only
   * "who am I", `GET me/session`: the `user`, `default_filter` and
   * `active_context` of LoginData and nothing else. A page load is not a
   * sign-in, so it must not look like one: `POST LoginData` writes a `login`
   * row in the sign-in log (builds without a cookie session) and counts
   * against the sign-in rate limit on every call.
   *
   * `POST LoginData` (what the sign-in kit calls after Cognito) is asked
   * instead, once, with the same token, when:
   * - there is no identity service (the gateway serves LoginData: local setups);
   * - `me/session` gives no usable answer: 404 (an identity service that does
   *   not have the route yet, e.g. during a staggered deploy), any status
   *   other than the ones below, a server error or no answer at all. The page
   *   load then ends exactly as it did before `me/session` existed.
   * `me/session` decides alone on 200 with a user, on 401 / 403 (the session
   * is refused: as LoginData refuses it) and on 429 (rate limited: no second
   * request, the caller may retry).
   *
   * Token handling is not changed: the token comes from getAuthToken() and
   * each request runs through fetchAuthenticatedWithSingleRetry like every
   * other authenticated call. Without a token nothing is sent (`signed_out`).
   * Neither request issues, refreshes or stores a token.
   *
   * - `ok`: `data` holds `user`, `default_filter`, `active_context`;
   * - `signed_out`: no token in this tab;
   * - `refused`: the server does not accept the session (401 / 403 / 404);
   * - `unavailable`: no answer, a server error or 429 (the caller may retry).
   */
  static async requestSessionUser(): Promise<SessionUserAnswer> {
    const token = await ApiProvider.getAuthToken();
    if (!token) return { status: 'signed_out' };

    const withSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);
    if (identityApiUrl) {
      const whoAmI = await ApiProvider.fetchSessionUser(
        'GET',
        `${withSlash(identityApiUrl)}me/session`,
        'GET me/session (session load)',
      );
      const decided =
        whoAmI.answer.status === 'ok' ||
        whoAmI.answer.status === 'signed_out' ||
        [401, 403, 429].includes(whoAmI.httpStatus);
      if (decided) return whoAmI.answer;
    }

    // where the kit's gateway client sends LoginData: `<identity>v1/`, else the gateway
    const base = identityApiUrl
      ? `${withSlash(identityApiUrl)}v1/`
      : withSlash(String(ApiProvider.apiUrl ?? ''));
    const loginData = await ApiProvider.fetchSessionUser(
      'POST',
      `${base}LoginData`,
      'POST LoginData (session load)',
    );
    return loginData.answer;
  }

  /**
   * One session-load request (requestSessionUser) and what its answer means.
   * `httpStatus` 0 = nothing was answered (not sent, or no response).
   */
  private static async fetchSessionUser(
    method: 'GET' | 'POST',
    url: string,
    requestLabel: string,
  ): Promise<{ answer: SessionUserAnswer; httpStatus: number }> {
    let httpStatus = 0;
    let payload: any;
    try {
      payload = await ApiProvider.fetchAuthenticatedWithSingleRetry(
        async (bearer) => {
          const response = await fetch(url, {
            method,
            cache: 'no-store',
            headers: { Authorization: `Bearer ${bearer}` },
          });
          httpStatus = response.status;
          return response;
        },
        { requestLabel },
      );
    } catch {
      return { answer: { status: 'unavailable' }, httpStatus: 0 };
    }
    if (payload?.success === true && payload?.data?.user) {
      return { answer: { status: 'ok', data: payload.data }, httpStatus };
    }
    // nothing was sent: the token went away while the request was prepared
    if (httpStatus === 0 && payload?.status === 401) {
      return { answer: { status: 'signed_out' }, httpStatus };
    }
    // a refusal is 401 / 403 / 404, or an answer without a user; anything else
    // (no answer, 5xx, 429 of the identity service's per-IP limit) is not a
    // reason to sign the user out
    const answered = httpStatus >= 200 && httpStatus < 300;
    if (answered || [401, 403, 404].includes(httpStatus)) {
      return { answer: { status: 'refused', httpStatus }, httpStatus };
    }
    return { answer: { status: 'unavailable' }, httpStatus };
  }

  /** Store the "session expired" notice the login page shows once. */
  static noteSessionExpired(): void {
    try {
      sessionStorage.setItem(
        ApiProvider.SESSION_EXPIRED_MESSAGE_KEY,
        ApiProvider.SESSION_EXPIRED_MESSAGE_TEXT,
      );
    } catch {
      // Ignore storage failures.
    }
  }

  /**
   * Authenticated call to the identity service (VITE_IDENTITY_API_URL, e.g.
   * `me/assignments`). Same token handling as the gateway helpers. Answers
   * `{success: false, status: 0, message: 'identity_api_not_configured'}`
   * when the URL is not set.
   */
  static async identityRequest(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: Record<string, unknown>,
  ): Promise<any> {
    if (!identityApiUrl) {
      return { success: false, status: 0, message: 'identity_api_not_configured' };
    }
    const base = identityApiUrl.endsWith('/') ? identityApiUrl : `${identityApiUrl}/`;
    const url = base + path.replace(/^\/+/, '');
    try {
      return await ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) =>
          roleAwareFetch(url, {
            method,
            cache: 'no-store',
            headers: ApiProvider.buildAuthHeaders(token, {
              accept: 'application/json',
              ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            }),
            body: body === undefined ? undefined : JSON.stringify(body),
          }),
        { requestLabel: `${method} identity ${path}` },
      );
    } catch (error: any) {
      return { success: false, status: 0, message: error?.message || 'Network error' };
    }
  }

  /**
   * Authenticated JSON call to another PNET service at `baseUrl` (the
   * notification service, VITE_NOTIFICATION_API_URL), with the same token
   * handling and headers as identityRequest (bearer, X-PNET-Expected-Assignment,
   * the role-change and preview rules of roleAwareFetch). Answers the HTTP
   * status next to the body, so a caller can tell a missing service (404 /
   * 501) from a refusal; `httpStatus` 0 = no answer (network failure, aborted,
   * or no token: the body then says why).
   */
  static async serviceCall(
    baseUrl: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<{ httpStatus: number; payload: any }> {
    const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const url = base + path.replace(/^\/+/, '');
    let httpStatus = 0;
    try {
      const payload = await ApiProvider.fetchAuthenticatedWithSingleRetry(
        async (token) => {
          const response = await roleAwareFetch(url, {
            method,
            cache: 'no-store',
            signal,
            headers: ApiProvider.buildAuthHeaders(token, {
              accept: 'application/json',
              ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            }),
            body: body === undefined ? undefined : JSON.stringify(body),
          });
          httpStatus = response.status;
          return response;
        },
        { requestLabel: `${method} ${path}` },
      );
      return { httpStatus, payload };
    } catch (error: any) {
      return {
        httpStatus: 0,
        payload: { success: false, status: 0, message: error?.message || 'Network error' },
      };
    }
  }

  /**
   * The landing-page modules of the active role (part 7m): identity's
   * `me/details` = {brand_module, property_module, home_module} only, not
   * the gateway's User/Details with the caller's brand / category / country /
   * partner-type id lists (which the UI never read). Without
   * VITE_IDENTITY_API_URL (local mock setups) the gateway route as before.
   */
  static userDetailsUrl(): string {
    if (!identityApiUrl) return ApiProvider.apiUrl + 'User/Details';
    const base = identityApiUrl.endsWith('/') ? identityApiUrl : `${identityApiUrl}/`;
    return base + 'me/details';
  }

  static get_user_access() {
    return (
      identityApiUrl
        ? ApiProvider.identityRequest('GET', 'me/details')
        : this.webserviceGet('User/Details')
    ).then(
      (response: any) => {
        if (response.success == true) {
          setSessionModules(response.data ?? {});
        }
      },
      (error) => {
        this.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  /**
   * The header brand when the active scope has exactly one brand (or one
   * parent brand). The list is the server's (Brands/GetBrands = brands of the
   * active scope); the client sends no access list of its own (part 7: the
   * old `BrandAccess` / `User/Brands?user_id=` load is gone).
   */
  static async getUserAccessBrand(setHeaderBrand: any) {
    try {
      const response: any = await ApiProvider.webserviceGet('Brands/GetBrands');
      if (!response.success) return;
      const brandAccess = response.data.results;
      const parentBrand = brandAccess.filter((item: any) => item.parent_id === null);
      const selectedBrand =
        brandAccess.length === 1
          ? brandAccess[0]
          : parentBrand.length === 1
            ? parentBrand[0]
            : null;

      if (selectedBrand) {
        setHeaderBrand(selectedBrand);
      }
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  static async getUserAccessProperty(setHeaderProperty: any) {
    try {
      const response: any = await ApiProvider.webservicePostToken(
        'Property/GetPropertyList',
        null,
        'yes',
      );

      if (!response.success) return;
      const results = response.data.results;
      const rightHolders = results.filter((item: any) => item.added_as === 'right_holder');

      const selectedProperty =
        results.length === 1 ? results[0] : rightHolders.length === 1 ? rightHolders[0] : null;

      if (selectedProperty) {
        setHeaderProperty(selectedProperty);
      }
      // the same list decides the role's start partner (N2)
      await ApiProvider.decideHomePartner(response.data);
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  /**
   * The start partner of the ACTIVE RIGHT_HOLDER role (open question N2, owner
   * 2026-10-13), from the server's partner list of the active scope
   * (`Property/GetPropertyList` without a filter, its first page):
   *
   * - the context's partner (the organisation's main partner) when it is
   *   inside the role's access: the start page as before;
   * - else the first partner inside the access (the one the header selects
   *   when the scope has a single partner / right holder, else the list's
   *   first row): a user limited to a child partner no longer starts on a
   *   "Not found" page.
   *
   * When the context's partner is not on the first page and the list has
   * more, the server is asked for that one partner (the filter only narrows
   * the scope). No answer: nothing is decided and the start page is as before.
   * The client sends no access list; the answer stays in memory (auth/landing.ts).
   */
  static async decideHomePartner(list: any): Promise<void> {
    const assignmentId = activeAssignmentId();
    const contextPartner = getActiveAssignment()?.propertyId ?? null;
    if (assignmentId === null || contextPartner === null) return;

    const results: any[] = Array.isArray(list?.results) ? list.results : [];
    const hasId = (rows: any[], id: number) => rows.some((row) => Number(row?.id) === id);
    let inside = hasId(results, contextPartner);
    const total = Number(list?.pagination?.total);
    const more =
      list?.pagination?.more === true || (Number.isFinite(total) && total > results.length);
    if (!inside && more) {
      const body = new FormData();
      body.append('page', '1');
      body.append('property_id[]', String(contextPartner));
      const response: any = await ApiProvider.webservicePostToken(
        'Property/GetPropertyList',
        body,
        'yes',
      );
      if (response?.success !== true) return;
      inside = hasId(
        Array.isArray(response.data?.results) ? response.data.results : [],
        contextPartner,
      );
    }
    // the role changed, or the user signed out, while the request ran
    if (activeAssignmentId() !== assignmentId || !isSignedIn()) return;

    if (inside) {
      setHomePartnerId(contextPartner, assignmentId);
      return;
    }
    const rightHolders = results.filter((item: any) => item?.added_as === 'right_holder');
    const first =
      results.length === 1 ? results[0] : rightHolders.length === 1 ? rightHolders[0] : results[0];
    const firstId = Number(first?.id);
    setHomePartnerId(Number.isInteger(firstId) && firstId > 0 ? firstId : null, assignmentId);
  }

  /**
   * Hard refresh: the start partner of a RIGHT_HOLDER role is in memory only,
   * so a page load decides it again (the header's home link). Nothing is
   * loaded for another context type, or when this page already holds it.
   */
  static async loadHomePartner(): Promise<void> {
    if (getActiveContextType() !== 'RIGHT_HOLDER' || getHomePartnerId() !== undefined) return;
    if ((getActiveAssignment()?.propertyId ?? null) === null) return;
    const response: any = await ApiProvider.webservicePostToken(
      'Property/GetPropertyList',
      null,
      'yes',
    );
    if (response?.success !== true) return;
    await ApiProvider.decideHomePartner(response.data);
  }

  /**
   * Where the active role lands (sign-in, role switch, preview start or end):
   * 1. the page to return to (`sessionurl`): a deep link opened while signed out, or the page a
   *    signed-in tab noted for THIS role (`sessionurl_ra`); never another role's page;
   * 2. the role's own last page (auth/rolePages.ts; the preview's own while a preview runs);
   * 3. the role's home (context type and context id, part 7 H4).
   * `canOpen` (auth/pathGates.ts canOpenPath) skips a page the role cannot open.
   */
  static getRedirectionUrl(
    user: { home_module?: any },
    canOpen: (page: string) => boolean = () => true,
  ): string {
    try {
      const candidates = [returnPageFor(activeAssignmentId()), readRolePage()];
      const page = candidates.find((candidate) => candidate !== null && canOpen(candidate));
      if (page) return page;

      // The active role's context type and context id (part 7, H4).
      return homePath(user?.home_module ?? null);
    } catch (error) {
      return '/dashboard';
    }
  }

  static addEllipsis(str: string, maxLength: number) {
    if (str.length > maxLength) {
      // Truncate the string to the specified maxLength
      str = str.substring(0, maxLength) + '...';
    }
    return str;
  }

  static formatToSB(value: number) {
    const format = (num: number, suffix: string) =>
      Number.isInteger(num) ? num + suffix : num.toFixed(2) + suffix;

    if (value >= 1_000_000_000_000) {
      return format(value / 1_000_000_000_000, 'T'); // Trillion
    } else if (value >= 1_000_000_000) {
      return format(value / 1_000_000_000, 'B'); // Billion
    } else if (value >= 1_000_000) {
      return format(value / 1_000_000, 'M'); // Million
    } else if (value >= 1_000) {
      return format(value / 1_000, 'K'); // Thousand
    }
    return value.toString();
  }

  /* ------------------ PROPERTY NODE API ------------------ */

  static getPropertyNodeUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/property-node`;
  }

  /**
   * Headers for the property-node API. `token` is the value resolved by
   * getAuthToken() (IdToken or AccessToken per VITE_AUTH_TOKEN_KIND); the
   * localStorage read is only a fallback for callers that did not resolve one.
   */
  private static getPropertyNodeHeaders(
    contentType = true,
    token: string | null = null,
  ): Record<string, string> {
    const headers: Record<string, string> = {
      accept: 'application/json',
    };

    if (contentType) {
      headers['Content-Type'] = 'application/json';
    }

    return ApiProvider.buildAuthHeaders(token ?? readAccessTokenCopy(), headers);
  }

  private static normalizePropertyNodeEndpoint(endpoint: string): string {
    if (!endpoint) return '';
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  static async propertyNodeGet<T = any>(
    endpoint: string,
    queryParams?: Record<string, string | number> | URLSearchParams | string | null,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    let query = '';
    if (queryParams) {
      const params =
        typeof queryParams === 'string'
          ? queryParams.replace(/^\?/, '')
          : queryParams instanceof URLSearchParams
            ? queryParams.toString()
            : new URLSearchParams(
                Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v)])),
              ).toString();
      query = params ? `?${params}` : '';
    }

    try {
      const response = await roleAwareFetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}${query}`,
        {
          method: 'GET',
          headers: this.getPropertyNodeHeaders(true, token),
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      // Property Node API wraps responses in { success, data, message }
      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodePost<T = any>(
    endpoint: string,
    body?: any,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await roleAwareFetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'POST',
          headers: this.getPropertyNodeHeaders(true, token),
          body: body ? JSON.stringify(body) : undefined,
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      // Property Node API wraps responses in { success, data, message }
      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodePatch<T = any>(
    endpoint: string,
    body?: any,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await roleAwareFetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'PATCH',
          headers: this.getPropertyNodeHeaders(true, token),
          body: body ? JSON.stringify(body) : undefined,
          signal: signal ?? undefined,
        },
      );

      return await this.parseResponsePayload(response);
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodePut<T = any>(
    endpoint: string,
    body?: any,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await roleAwareFetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'PUT',
          headers: this.getPropertyNodeHeaders(true, token),
          body: body ? JSON.stringify(body) : undefined,
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodeDelete<T = any>(
    endpoint: string,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await roleAwareFetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'DELETE',
          headers: this.getPropertyNodeHeaders(true, token),
        },
      );

      return await this.parseResponsePayload(response);
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  /* ---------------- STRATEGIC CLUSTERS API ---------------- */

  private static getStrategicClustersApiUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/Activation/strategic-clusters`;
  }

  private static normalizeStrategicClustersEndpoint(endpoint: string): string {
    if (!endpoint) return '';
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  private static async strategicClustersRequest<T = any>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    endpoint: string,
    body?: object,
    queryParams?: Record<string, string | number | null | undefined>,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const query = new URLSearchParams(
      Object.entries(queryParams ?? {})
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    const url = `${this.getStrategicClustersApiUrl()}${this.normalizeStrategicClustersEndpoint(endpoint)}${query ? `?${query}` : ''}`;

    try {
      return await this.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers = ApiProvider.buildAuthHeaders(token, {
            accept: 'application/json',
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          });

          return roleAwareFetch(url, {
            method,
            cache: 'no-store',
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
          });
        },
        {
          requestLabel: `${method} strategic-clusters${this.normalizeStrategicClustersEndpoint(endpoint)}`,
        },
      );
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static strategicClustersGet<T = any>(
    endpoint = '',
    queryParams?: Record<string, string | number | null | undefined>,
  ) {
    return this.strategicClustersRequest<T>('GET', endpoint, undefined, queryParams);
  }

  static strategicClustersPost<T = any>(endpoint = '', body?: object) {
    return this.strategicClustersRequest<T>('POST', endpoint, body);
  }

  static strategicClustersPatch<T = any>(endpoint: string, body?: object) {
    return this.strategicClustersRequest<T>('PATCH', endpoint, body);
  }

  static strategicClustersPut<T = any>(endpoint: string, body?: object) {
    return this.strategicClustersRequest<T>('PUT', endpoint, body);
  }

  static strategicClustersDelete<T = any>(
    endpoint: string,
    queryParams?: Record<string, string | number | null | undefined>,
  ) {
    return this.strategicClustersRequest<T>('DELETE', endpoint, undefined, queryParams);
  }

  /* ------------- ACTIVITY COUNTRY ASSIGNMENTS API ------------- */

  private static getActivityCountryAssignmentsApiUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/Activation/activity-country-assignments`;
  }

  private static async activityCountryAssignmentsRequest<T = any>(
    method: 'GET' | 'PATCH',
    endpoint: string,
    body?: object,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    try {
      return await this.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers = ApiProvider.buildAuthHeaders(token, {
            accept: 'application/json',
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          });
          return roleAwareFetch(
            `${this.getActivityCountryAssignmentsApiUrl()}${normalizedEndpoint}`,
            {
              method,
              cache: 'no-store',
              headers,
              body: body === undefined ? undefined : JSON.stringify(body),
            },
          );
        },
        { requestLabel: `${method} activity-country-assignments${normalizedEndpoint}` },
      );
    } catch (error: any) {
      return { success: false, message: error?.message || 'Network error', status: 0 };
    }
  }

  static activityCountryAssignmentsGet<T = any>(endpoint: string) {
    return this.activityCountryAssignmentsRequest<T>('GET', endpoint);
  }

  static activityCountryAssignmentsPatch<T = any>(endpoint: string, body: object) {
    return this.activityCountryAssignmentsRequest<T>('PATCH', endpoint, body);
  }

  /* ---------------- STORE PERFORMANCE API ---------------- */

  private static getStorePerformanceApiUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/Activation/head-to-head/store-performance`;
  }

  private static normalizeStorePerformanceEndpoint(endpoint: string): string {
    if (!endpoint) return '';
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  private static async storePerformanceRequest<T = any>(
    endpoint = '',
    queryParams?: Record<string, string | number | null | undefined>,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const query = new URLSearchParams(
      Object.entries(queryParams ?? {})
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    const normalizedEndpoint = this.normalizeStorePerformanceEndpoint(endpoint);
    const url = `${this.getStorePerformanceApiUrl()}${normalizedEndpoint}${query ? `?${query}` : ''}`;

    try {
      return await this.fetchAuthenticatedWithSingleRetry(
        (token) => {
          return roleAwareFetch(url, {
            method: 'GET',
            cache: 'no-store',
            headers: ApiProvider.buildAuthHeaders(token, { accept: 'application/json' }),
          });
        },
        { requestLabel: `GET store-performance${normalizedEndpoint}` },
      );
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static storePerformanceGet<T = any>(
    endpoint = '',
    queryParams?: Record<string, string | number | null | undefined>,
  ) {
    return this.storePerformanceRequest<T>(endpoint, queryParams);
  }

  /* ------------------- COMPETITION API ------------------- */

  private static getCompetitionApiUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/Activation/head-to-head/competition`;
  }

  static async competitionGet<T = any>(
    queryParams?: Record<string, string | number | null | undefined>,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const query = new URLSearchParams(
      Object.entries(queryParams ?? {})
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    ).toString();
    const url = `${this.getCompetitionApiUrl()}${query ? `?${query}` : ''}`;

    try {
      return await this.fetchAuthenticatedWithSingleRetry(
        (token) => {
          return roleAwareFetch(url, {
            method: 'GET',
            cache: 'no-store',
            headers: ApiProvider.buildAuthHeaders(token, { accept: 'application/json' }),
          });
        },
        { requestLabel: 'GET competition' },
      );
    } catch (error: any) {
      return { success: false, message: error?.message || 'Network error', status: 0 };
    }
  }

  /* ------------------ AUTHENTICATION END ------------------ */

  static async logoutUserLocally(navigationStatus: any, options: { globalSignOut?: boolean } = {}) {
    // One sign-out at a time; the error screens stay blank while it runs.
    if (isSigningOut()) return;
    markSigningOut();
    const shouldGlobalSignOut = options.globalSignOut === true;

    // Clear all localStorage FIRST — before signOut, because with OAuth
    // configured signOut() triggers an immediate browser redirect to Cognito's
    // /logout endpoint. Any code after `await signOut()` never runs in that
    // case. Clearing here (before the first await) runs synchronously and
    // ensures auth state is clean when the browser lands back at the app.
    // Session and every role-dependent key, incl. saved filters and
    // `sessionurl` (auth/clearSession.ts); the other tabs of this browser
    // are told, their in-memory session would otherwise outlive the sign-out.
    announceSignOut(getSessionUser()?.id);
    clearSession('signout');

    // Capture current location before signOut so we can detect whether
    // Amplify's signOut() initiated a browser redirect (OAuth /logout flow).
    // signOut() calls window.location.assign() synchronously inside its async
    // body, which updates window.location.href immediately even though the
    // page hasn't loaded yet. If the href changed, the browser is already
    // navigating — running window.location.href = '/' afterwards would race
    // with that redirect and cause a double page-load (the "constantly
    // refreshing login page" bug seen after password-user logout in an
    // OAuth-configured app).
    const hrefBeforeSignOut = window.location.href;

    const authSession = getAuthSession();
    if (authSession) {
      // The shared kit's sign-out: the server Logout (identity service
      // `v1/Logout`, or the gateway while VITE_IDENTITY_API_URL is unset; with
      // `all_sessions=1` for "sign out all devices"), in cookie mode instead
      // `session/logout` (RevokeToken + the httpOnly cookie cleared), then
      // Amplify's signOut. With OAuth configured it may redirect via Cognito
      // /logout, like the direct call below.
      try {
        await authSession.signOut({ everywhere: shouldGlobalSignOut });
      } catch (error) {
        console.error('Sign out error:', error);
      }
    } else if (!isMockAuthEnabled) {
      // Default logout should sign out only the current device/session.
      // Use `{ globalSignOut: true }` for explicit "sign out all devices".
      // With OAuth configured, either path may redirect via Cognito /logout.
      try {
        if (shouldGlobalSignOut) {
          await signOut({ global: true });
        } else {
          await signOut();
        }
      } catch (error) {
        console.error('Cognito sign out error:', error);
      }
    }

    // Only navigate manually when signOut() did NOT already start a browser
    // redirect (i.e. non-OAuth environments, mock mode, or when the OAuth
    // redirect was suppressed by an error). If Amplify's signOut() changed
    // the location we let that navigation complete and skip this fallback.
    if (navigationStatus && window.location.href === hrefBeforeSignOut) {
      window.location.href = '/';
    }

    return;
  }
}

// Every change of the header selection is kept on the server (debounced in
// selection/headerSelection.ts, which cannot import this module).
setHeaderSelectionSaver((selection, assignmentId) =>
  ApiProvider.saveHeaderSelection(selection, assignmentId),
);
